import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Building2 } from 'lucide-react';
import { configureAmplifyForUserType } from '../amplify-config';

const UserTypeSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if coming from signup or login
  const isSignup = location.state?.isSignup ?? true;

  const handleSelectUserType = (userType: 'personal' | 'business') => {
    // Configure Amplify with the selected user pool
    configureAmplifyForUserType(userType);
    
    // Navigate to signup or login based on context
    if (isSignup) {
      navigate('/signup');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="welcome-page">
      {/* Header */}
      <div style={{ marginBottom: '3rem', animation: 'slideDown 0.6s ease-out' }}>
        <h1 className="welcome-title" style={{ marginBottom: '1rem' }}>
          Choose Your Account Type
        </h1>
        <p className="welcome-description" style={{ marginBottom: 0 }}>
          Select the account type that best fits your needs
        </p>
      </div>

      {/* Cards Container */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '2rem',
        maxWidth: '900px',
        width: '100%',
        animation: 'scaleIn 0.5s ease-out 0.2s both'
      }}>
        {/* Personal User Card */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '3rem 2rem',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-8px)';
          e.currentTarget.style.boxShadow = '0 25px 70px rgba(0, 0, 0, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3)';
        }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <User style={{ width: '80px', height: '80px', color: '#667eea' }} />
          </div>
          
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            Personal User
          </h2>
          
          <p style={{
            color: '#666',
            textAlign: 'center',
            marginBottom: '2rem',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            Perfect for Personal or Home users
          </p>
          
          <button
            onClick={() => handleSelectUserType('personal')}
            className="btn btn-primary"
            style={{ 
              width: '100%', 
              fontSize: '1.1rem', 
              padding: '1rem 2rem',
              backgroundColor: '#ffc107',
              background: '#ffc107',
              color: '#000',
              border: 'none',
              fontWeight: '600'
            }}
          >
            {isSignup ? 'Get Started' : 'Sign In'}
          </button>
        </div>

        {/* Business User Card */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '3rem 2rem',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-8px)';
          e.currentTarget.style.boxShadow = '0 25px 70px rgba(0, 0, 0, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.3)';
        }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <Building2 style={{ width: '80px', height: '80px', color: '#667eea' }} />
          </div>
          
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '1rem',
            textAlign: 'center'
          }}>
            Business User
          </h2>
          
          <p style={{
            color: '#666',
            textAlign: 'center',
            marginBottom: '2rem',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            Ideal for all organisation sizes
          </p>
          
          <button
            onClick={() => handleSelectUserType('business')}
            className="btn btn-primary"
            style={{ 
              width: '100%', 
              fontSize: '1.1rem', 
              padding: '1rem 2rem',
              backgroundColor: '#ffc107',
              background: '#ffc107',
              color: '#000',
              border: 'none',
              fontWeight: '600'
            }}
          >
            {isSignup ? 'Get Started' : 'Sign In'}
          </button>
        </div>
      </div>

      {/* Back Link */}
      <div style={{ marginTop: '2.5rem', animation: 'fadeIn 0.8s ease-in 0.4s both' }}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: '1rem',
            fontWeight: '500',
            cursor: 'pointer',
            textDecoration: 'underline',
            transition: 'opacity 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          ← Back to Home
        </button>
      </div>
    </div>
  );
};

export default UserTypeSelectionPage;
